// CTSK-364 (CTSK-353 fold) — inline editor for one Bookmarks row.
// Renders as a full-width expansion row under the bookmark being
// edited (BookmarksTable owns the "only one editor open at a time"
// state; this component owns the form).
//
// Contract:
//   - Editable fields mirror the operator-authored subset of
//     BookmarkEntrySchema: title, url, note, tags. Machine-stamped
//     fields (id, captured_at, source) and machine-captured ones
//     (external_refs) are display-only / untouched — the PATCH body
//     only ever carries fields the operator can legitimately edit.
//   - Save PATCHes via patchRegistryEntry (partial merge server-side)
//     and reports the merged entry through onSaved; the parent
//     refetches the YAML so the table re-renders from the persisted
//     bytes, not from optimistic state.
//   - Validation is field-level Zod (the same BookmarkEntrySchema
//     rules the server enforces on the merged entry) — Save disables
//     while any field is invalid, and per-field messages render
//     inline. Clearing note/tags is legal (optional fields — the
//     PATCH sends null to delete, so the YAML never accumulates
//     `note: null` rows).
//   - Cancel collapses without a write.

import { useMemo, useState } from 'react';
import { patchRegistryEntry, ApiError } from '../lib/api.js';
import { toast } from './Toast.js';

/** The row shape the editor needs — matches BookmarksTable's Bookmark. */
export interface EditableBookmark {
  id: string;
  url: string;
  title: string;
  note?: string;
  tags?: string[];
  /** CTSK-368 — hyper_scan.{uuid,card_url} are operator-editable in
   *  this editor; other namespaces / fields stay machine-captured. */
  external_refs?: Record<string, Record<string, unknown>>;
}

export interface BookmarkRowEditorProps {
  bookmark: EditableBookmark;
  /** Tool id + registered file path for the PATCH route. */
  toolId: string;
  filePath: string;
  /** Fires with the server's merged entry after a successful save —
   *  the parent should refetch the registry YAML. */
  onSaved: (entry: Record<string, unknown>) => void;
  onCancel: () => void;
}

// Field-level validators, mirroring BookmarkEntrySchema's rules
// (title min 1, url must parse as a URL). Plain predicates — the web
// app doesn't depend on zod directly; the server still validates the
// MERGED entry against the shared schema as the source of truth.
function validateTitle(title: string): string | null {
  return title.length > 0 ? null : 'Title is required';
}
function validateUrl(url: string): string | null {
  // Same accept-set as z.string().url(): any parseable absolute URL.
  try {
    new URL(url);
    return null;
  } catch {
    return 'Must be a valid URL (https://…)';
  }
}

export function BookmarkRowEditor({
  bookmark,
  toolId,
  filePath,
  onSaved,
  onCancel,
}: BookmarkRowEditorProps) {
  const [title, setTitle] = useState(bookmark.title);
  const [url, setUrl] = useState(bookmark.url);
  const [note, setNote] = useState(bookmark.note ?? '');
  const [tagsText, setTagsText] = useState((bookmark.tags ?? []).join(', '));
  // CTSK-368 — hyper-scanner refs. The scan action captures these via
  // fetch-and-map (CTSK-364); the editor lets the operator attach a
  // scan done outside the flow or correct/clear a stale capture.
  // audit_url / status_url stay machine-captured (read-only context
  // below; preserved by the server's field-level merge).
  const prevHyperScan = bookmark.external_refs?.hyper_scan;
  const [hsUuid, setHsUuid] = useState(typeof prevHyperScan?.uuid === 'string' ? prevHyperScan.uuid : '');
  const [hsCardUrl, setHsCardUrl] = useState(typeof prevHyperScan?.card_url === 'string' ? prevHyperScan.card_url : '');
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const errors = useMemo(() => {
    const out: Partial<Record<'title' | 'url' | 'hsCardUrl', string>> = {};
    const titleError = validateTitle(title.trim());
    if (titleError) out.title = titleError;
    const urlError = validateUrl(url.trim());
    if (urlError) out.url = urlError;
    if (hsCardUrl.trim().length > 0 && validateUrl(hsCardUrl.trim())) {
      out.hsCardUrl = 'Must be a valid URL (https://…)';
    }
    return out;
  }, [title, url, hsCardUrl]);
  const valid = Object.keys(errors).length === 0;

  const save = async () => {
    if (!valid || saving) return;
    setSaving(true);
    setServerError(null);
    // Comma-separated → trimmed, de-duplicated (order-preserving) —
    // the same shape hyper-scanno + the clipper emit.
    const seen = new Set<string>();
    const tags = tagsText.split(',')
      .map((t) => t.trim())
      .filter((t) => t.length > 0 && !seen.has(t) && (seen.add(t) || true));
    const patch: Record<string, unknown> = {
      title: title.trim(),
      url: url.trim(),
      // null deletes the optional field server-side; the YAML never
      // carries `note: ""` / `tags: []` husks.
      note: note.trim().length > 0 ? note.trim() : null,
      tags: tags.length > 0 ? tags : null,
    };
    // CTSK-368 — hyper-scanner refs. Field-level nulls delete just
    // that field (server merge preserves audit/status siblings); both
    // cleared on a previously-scanned row → the whole namespace goes.
    const uuidVal = hsUuid.trim().length > 0 ? hsUuid.trim() : null;
    const cardVal = hsCardUrl.trim().length > 0 ? hsCardUrl.trim() : null;
    const hadHyperScan = prevHyperScan !== undefined;
    if (uuidVal === null && cardVal === null) {
      if (hadHyperScan) patch.external_refs = { hyper_scan: null };
      // never scanned + still empty → nothing to send
    } else {
      patch.external_refs = { hyper_scan: { uuid: uuidVal, card_url: cardVal } };
    }
    try {
      const res = await patchRegistryEntry(toolId, filePath, bookmark.id, patch);
      toast.success(`Bookmark '${bookmark.id}' saved`);
      onSaved(res.entry);
    } catch (err) {
      // Surface the server's ENTRY_VALIDATION_FAILED issues when
      // present — the merged-entry check can fail for reasons the
      // field-level rules can't see (e.g. a schema tightened since
      // this build shipped).
      const message = err instanceof ApiError ? err.message : (err as Error).message;
      setServerError(message);
      toast.error(`Save failed: ${message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      role="form"
      aria-label={`Edit bookmark ${bookmark.id}`}
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(220px, 1fr) minmax(220px, 1fr)',
        gap: '10px 16px',
        padding: '12px 14px',
        background: 'color-mix(in srgb, var(--accent-primary) 5%, var(--surface-raised))',
        border: '1px solid color-mix(in srgb, var(--accent-primary) 30%, var(--border-subtle))',
        borderRadius: 'var(--radius-md)',
      }}
    >
      <Field label="Title" error={errors.title}>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          aria-label="Title"
          aria-invalid={errors.title ? true : undefined}
          style={inputStyle(Boolean(errors.title))}
        />
      </Field>
      <Field label="URL" error={errors.url}>
        <input
          type="text"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          aria-label="URL"
          aria-invalid={errors.url ? true : undefined}
          style={{ ...inputStyle(Boolean(errors.url)), fontFamily: 'var(--font-mono)', fontSize: 12 }}
        />
      </Field>
      <Field label="Tags (comma-separated)">
        <input
          type="text"
          value={tagsText}
          onChange={(e) => setTagsText(e.target.value)}
          aria-label="Tags"
          placeholder="architecture, design, reference"
          style={inputStyle(false)}
        />
      </Field>
      <Field label="Note">
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          aria-label="Note"
          style={inputStyle(false)}
        />
      </Field>

      {/* CTSK-368 — hyper-scanner refs (drives the Card column). */}
      <Field label="Scanner UUID">
        <input
          type="text"
          value={hsUuid}
          onChange={(e) => setHsUuid(e.target.value)}
          aria-label="Scanner UUID"
          placeholder="d2bcf120-…"
          style={{ ...inputStyle(false), fontFamily: 'var(--font-mono)', fontSize: 12 }}
        />
      </Field>
      <Field label="Card URL" error={errors.hsCardUrl}>
        <input
          type="text"
          value={hsCardUrl}
          onChange={(e) => setHsCardUrl(e.target.value)}
          aria-label="Card URL"
          aria-invalid={errors.hsCardUrl ? true : undefined}
          placeholder="http://hyper-scan.chadwiki.loc:8765/card/…"
          style={{ ...inputStyle(Boolean(errors.hsCardUrl)), fontFamily: 'var(--font-mono)', fontSize: 12 }}
        />
      </Field>
      {(typeof prevHyperScan?.audit_url === 'string' || typeof prevHyperScan?.status_url === 'string') && (
        <div style={{ gridColumn: '1 / -1', fontSize: 11, color: 'var(--text-muted)' }}>
          machine-captured (read-only, preserved on save):{' '}
          {typeof prevHyperScan?.audit_url === 'string' && <>audit_url: <code style={{ fontFamily: 'var(--font-mono)' }}>{prevHyperScan.audit_url}</code>{' '}</>}
          {typeof prevHyperScan?.status_url === 'string' && <>status_url: <code style={{ fontFamily: 'var(--font-mono)' }}>{prevHyperScan.status_url}</code></>}
        </div>
      )}

      {serverError && (
        <div role="alert" style={{ gridColumn: '1 / -1', fontSize: 12, color: 'var(--danger-primary, #B3261E)' }}>
          {serverError}
        </div>
      )}

      <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          style={{ padding: '5px 14px', fontSize: 12, border: '1px solid var(--border-strong)', borderRadius: 6, background: 'var(--surface-raised)', color: 'var(--text-primary)', cursor: 'pointer', fontFamily: 'inherit' }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => { void save(); }}
          disabled={!valid || saving}
          style={{
            padding: '5px 14px',
            fontSize: 12,
            border: '1px solid var(--accent-primary)',
            borderRadius: 6,
            background: (!valid || saving) ? 'color-mix(in srgb, var(--accent-primary) 35%, var(--surface-raised))' : 'var(--accent-primary)',
            color: 'var(--accent-on-primary, #fff)',
            cursor: (!valid || saving) ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {label}
      {children}
      {error && (
        <span role="alert" style={{ fontSize: 11, textTransform: 'none', letterSpacing: 'normal', color: 'var(--danger-primary, #B3261E)' }}>
          {error}
        </span>
      )}
    </label>
  );
}

function inputStyle(invalid: boolean): React.CSSProperties {
  return {
    padding: '6px 8px',
    fontSize: 13,
    border: `1px solid ${invalid ? 'var(--danger-primary, #B3261E)' : 'var(--border-strong)'}`,
    borderRadius: 6,
    background: 'var(--surface-base, var(--surface-raised))',
    color: 'var(--text-primary)',
    fontFamily: 'inherit',
    textTransform: 'none',
    letterSpacing: 'normal',
  };
}
